import { type RefObject, useEffect, useRef, useState } from "react"

interface Options {
	/** When false, the hook is a no-op (e.g. chat panel collapsed). */
	enabled: boolean
	/** Id of the newest message currently in view. */
	newestMessageId: string | null
	/** True when the newest message was authored by the viewer (then we skip — don't ack own messages). */
	newestActorIsSelf: boolean
	/**
	 * Optional viewport-intersection gate. When provided, auto-ack only fires
	 * while the referenced element is intersecting the viewport. Omit for
	 * surfaces that conditionally mount (e.g. a chat panel that only exists
	 * when open) — the mount itself is the visibility signal.
	 */
	inViewRef?: RefObject<HTMLElement | null>
	onAck: (messageId: string) => void
}

/**
 * Fires `onAck(newestMessageId)` once whenever a new (non-self) message
 * becomes the newest in the chat AND the window has focus AND (when
 * `inViewRef` is provided) the referenced element is in the viewport.
 *
 * Also re-evaluates on window focus and `visibilitychange` so a tab that
 * returns to focus immediately acks the latest pending message.
 *
 * Each messageId is acked at most once per mount (client-side dedupe);
 * the server's monotonic cursor would no-op duplicates anyway, but
 * skipping the request keeps the network quiet.
 */
export function useAutoAckOnArrival({
	enabled,
	newestMessageId,
	newestActorIsSelf,
	inViewRef,
	onAck,
}: Options): void {
	const lastAckedRef = useRef<string | null>(null)
	const onAckRef = useRef(onAck)
	useEffect(() => {
		onAckRef.current = onAck
	}, [onAck])

	const [windowFocused, setWindowFocused] = useState<boolean>(() =>
		typeof document !== "undefined" ? document.hasFocus() : true,
	)
	const [inView, setInView] = useState<boolean>(() => !inViewRef)

	useEffect(() => {
		const onFocus = () => setWindowFocused(true)
		const onBlur = () => setWindowFocused(false)
		const onVisibility = () =>
			setWindowFocused(!document.hidden && document.hasFocus())
		window.addEventListener("focus", onFocus)
		window.addEventListener("blur", onBlur)
		document.addEventListener("visibilitychange", onVisibility)
		return () => {
			window.removeEventListener("focus", onFocus)
			window.removeEventListener("blur", onBlur)
			document.removeEventListener("visibilitychange", onVisibility)
		}
	}, [])

	useEffect(() => {
		if (!inViewRef) return
		const el = inViewRef.current
		if (!el) return
		const io = new IntersectionObserver(
			(entries) => setInView(entries[0]?.isIntersecting ?? false),
			{ threshold: 0.1 },
		)
		io.observe(el)
		return () => io.disconnect()
	}, [inViewRef])

	useEffect(() => {
		if (!enabled) return
		if (newestActorIsSelf) return
		if (!newestMessageId) return
		if (lastAckedRef.current === newestMessageId) return
		if (!windowFocused) return
		if (!inView) return
		lastAckedRef.current = newestMessageId
		onAckRef.current(newestMessageId)
	}, [enabled, newestMessageId, newestActorIsSelf, windowFocused, inView])
}
